import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../css/consultant.css";
import PdfOpenLink from "./common/PdfOpenLink";

const Consultant = () => {
  const navigate = useNavigate();
  const [aiResult, setAiResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [judgement, setJudgement] = useState({});
  const [normalizedScores, setNormalizedScores] = useState({});
  const [userInfo, setUserInfo] = useState({});
  const location = useLocation(); // ← 追加
  const loginCode = localStorage.getItem("loggedInToken");

  // スコア → レベル変換関数
  const scoreToLevel = (score) => {
    if (score >= 125) return "A";
    if (score >= 110) return "B";
    if (score > 100) return "C+";
    if (score >= 90) return "C";
    if (score >= 75) return "D";
    return "E";
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const res = await fetch("https://mpzzlvohx7.execute-api.ap-northeast-3.amazonaws.com/prod/api/consultant/prelist", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "fetch-consultant-result",
            login_code: loginCode,
          }),
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || errorData.message || `HTTP ${res.status}: ${res.statusText}`);
        }
        
        const data = await res.json();
        if (data.error) throw new Error(data.error);

        setJudgement(data.judgement);
        setNormalizedScores(data.normalizedScores);
        console.log("normalizedScores:", data.normalizedScores);

        // location.state のデータで userInfo を上書き（ある場合のみ）
        const userNameFromRoute = location.state?.userName;
        const affiliationFromRoute = location.state?.affiliation;
        
        // 拠点名を取得（locationNamesを優先、なければcompanyName_findjob）
        const locationNamesStr = localStorage.getItem("locationNames");
        let locationName = null;
        if (locationNamesStr) {
          try {
            const locationNames = JSON.parse(locationNamesStr);
            if (Array.isArray(locationNames) && locationNames.length > 0) {
              locationName = locationNames.join('、'); // 複数拠点の場合は「、」で結合
            } else if (typeof locationNames === 'string') {
              locationName = locationNames;
            }
          } catch (e) {
            // JSON解析に失敗した場合は文字列として扱う
            locationName = locationNamesStr;
          }
        }
        
        // 拠点名が取得できない場合は企業名をフォールバック
        const affiliation = affiliationFromRoute || locationName || localStorage.getItem("companyName_findjob");
        const userAddress = localStorage.getItem("userAddress");

        setUserInfo({
          ...data.userInfo,
          ...(userNameFromRoute && { name: userNameFromRoute }),
          ...(affiliation && { affiliation: affiliation }),
          ...(userAddress && { address: userAddress }),

        });

      } catch (err) {
        console.error("診断データの取得エラー:", err);
        const errorMessage = err.message || "診断データの取得に失敗しました";
        alert("診断データの取得に失敗しました: " + errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [loginCode, location.state]); // location.state も依存配列に

  // AI診断実行
  const handleAnalyzeWithAI = async () => {
    setLoading(true);
    try {
      // judgementLevel を作成
      const judgementLevel = {};
      for (const [key, score] of Object.entries(judgement)) {
        judgementLevel[key] = scoreToLevel(score);
      }
      
      const response = await fetch("https://mpzzlvohx7.execute-api.ap-northeast-3.amazonaws.com/prod/api/consultant/result", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          judgement: judgementLevel,
          scores: normalizedScores,
          login_code: loginCode,
          userInfo,
        }),
      });

      const result = await response.json();
      setAiResult(result);
    } catch (error) {
      console.error("AI診断に失敗しました:", error);
      alert("AI診断に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="consultant-container">
      <h1>{userInfo.name}さん（所属：{userInfo.affiliation}）の<br />適職コンサルティング</h1>

      <section>
        <h2>GATBスコア評価</h2>
        <table className="score-table">
          <tbody>
            {Object.entries(judgement).map(([key, value]) => {
              const labels = {
                G: "知的分野（General learning ability）",
                V: "言語分野（Verbal ability）",
                N: "数理分野（Numerical ability）",
                Q: "書記分野（Clerical perception）",
                S: "空間分野（Spatial aptitude）",
                P: "形態分野（Form perception）",
              };
              const level = scoreToLevel(value);
              const levelClass = level.replace("+", "plus");

              return (
                <tr key={key}>
                  <td>{key}（{labels[key] || "不明"}）</td>
                  <td><span className={`badge badge-${levelClass}`}>レベル{level}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section>
        <h2>興味分野スコア（%）</h2>
        <table className="score-table">
          <tbody>
            {Object.entries(normalizedScores).map(([category, score]) => {
              const labels = {
                realistic: "R（現実的）",
                investigative: "I（研究的）",
                artistic: "A（芸術的）",
                social: "S（社会的）",
                enterprising: "E（企業的）",
                conventional: "C（慣習的）",
              };
              return (
                <tr key={category}>
                  <td>{labels[category] || category}</td>
                  <td>{parseInt(score, 10)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <div className="button-area">
        <button onClick={handleAnalyzeWithAI} className="button" disabled={loading}>
          {loading ? "診断中..." : "AIで分析する"}
        </button>
      </div>

      {aiResult && (
        <section>
          <h2>AIによる最終診断結果</h2>
          <p><strong>最適職業:</strong> {aiResult.top_job}</p>
          <p><strong>推奨職業:</strong> {aiResult.recommended_jobs?.join(", ")}</p>

          <p><strong>推奨職種（カテゴリ）:</strong></p>
          <ul>
            {aiResult.recommended_job_types?.map((jt, idx) => (
              <li key={idx}>・{jt.type}：{jt.description}</li>
            ))}
          </ul>

          <p><strong>推奨業種（業界）:</strong></p>
          <ul>
            {aiResult.recommended_industries?.map((ind, idx) => (
              <li key={idx}>・{ind.industry}：{ind.description}</li>
            ))}
          </ul>

          <p><strong>必要スキル:</strong> {aiResult.required_skills?.join(", ")}</p>
          <p><strong>学習方法:</strong> {aiResult.training}</p>
          <p><strong>理由:</strong> {aiResult.explanation}</p>

          {aiResult.pdf_url && (
            <div className="button-area" style={{ marginTop: "1rem" }}>
              <PdfOpenLink url={aiResult.pdf_url} />
            </div>
          )}

          <button
            className="job-search-button"
            onClick={() => {
              const Address = userInfo.address || "";
              const jobType = aiResult.recommended_job_types?.map(j => j.type).join(" ");
              const query = `${Address} ${aiResult.top_job} ${jobType} 求人`;
              window.open(
                `https://www.google.com/maps/search/${encodeURIComponent(query)}`,
                "_blank",
                "noopener noreferrer"
              );
            }}
          >
            Googleで探す
          </button>
        </section>
      )}

      <div className="button-area">
        <button onClick={() => navigate("/Portal")} className="button">
          ポータルに戻る
        </button>
      </div>
    </div>
  );
};

export default Consultant;
